import { graphql } from 'react-relay';

import { expValEquals } from '@confluence/feature-experiments';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { SYSTEM_CONTENT_BYLINE } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import { preloadCollabDraftContent } from '@confluence/load-edit-page/entry-points/preloadNativeEditor.ts';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { preloadEditorNodeDataProvidersData } from '@confluence/load-edit-page/entry-points/preloadEditorNodeDataProvidersData.ts';
import { preloadMentionsProvider } from '@confluence/fabric-media-support/entry-points/preloadMentions.ts';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime.ts';
import { preloadMacroDataFromCollabDraft } from '@confluence/preload-macros/entry-points/preloadMacroData.ts';
import { fg } from '@confluence/feature-gating';
import { NativeCollabQuery } from '@confluence/native-collab/entry-points/NativeCollabQuery.graphql.ts';
import type {
	NativeCollabQueryResponse,
	NativeCollabQueryVariables,
} from '@confluence/native-collab/entry-points/NativeCollabQuery.ts';
import { preloadContentMode } from '@confluence/editor-content-mode-button/entry-points/preloadContentMode.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';

import type {
	preloadLivePageDataQuery$data,
	preloadLivePageDataQuery$variables,
} from './__generated__/preloadLivePageDataQuery.graphql.ts';

const preloadLivePageDataQuery = graphql`
	query preloadLivePageDataQuery(
		$userId: ID
		$contentId: ID!
		$spaceKey: String!
		$isUsdSsr: Boolean!
	) {
		content(id: $contentId, status: ["current", "draft"]) {
			nodes {
				id
				status
				type
				emojiTitlePublished: properties(key: "emoji-title-published") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				# emojiTitleDraft is necessary for SSR parity with EmojiTitleQueryArtifact
				# even though live pages do not utilize this field
				emojiTitleDraft: properties(key: "emoji-title-draft") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				contentProperties {
					latest {
						defaultTitleEmoji
					}
				}
				properties(key: "content-appearance-draft") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				metadata {
					frontend {
						coverPictureWidth
					}
					currentuser {
						favourited {
							isFavourite
						}
					}
				}
				contentState(isDraft: false) {
					id
					name
					color
					restrictionLevel
					isCallerPermitted
				}
				contentStateLastUpdated(format: USER_FRIENDLY) {
					value
				}
				operations {
					operation
					targetType
				}
				subType
				version {
					number
					ncsStepVersion
					by {
						type
						... on KnownUser {
							accountId
						}
					}
				}
				labels {
					nodes {
						id
						name
					}
				}
			}
		}
		space(key: $spaceKey) {
			id
			settings {
				customHeaderAndFooter {
					header {
						html
						js
						css
						spaUnfriendlyMacros {
							name
						}
					}
					footer {
						html
						js
						css
						spaUnfriendlyMacros {
							name
						}
					}
				}
			}
		}
		availableContentStates(contentId: $contentId) {
			customContentStates {
				name
				color
				id
			}
			spaceContentStates {
				name
				color
				id
				restrictionLevel
			}
		}
		publicLinkPage(pageId: $contentId) @include(if: $isUsdSsr) {
			pageId
			pageStatus
		}
		user: confluence_atlassianUser(id: $userId) {
			id
			displayName
			photos {
				value
				isPrimary
			}
		}
	}
`;

export type PreloadLivePageDataParams = {
	userId: string | null;
	contentId: string;
	spaceKey: string;
	cloudId?: string;
};

/**
 * We combine what we can into a single unified query.
 * More complex preloaders will be split out for now.
 */
export function preloadLivePageData({
	userId,
	contentId,
	spaceKey,
	cloudId,
}: PreloadLivePageDataParams) {
	const preloaders: Array<Promise<any>> = [];

	// 1. Editor data

	// specific native collab editor queries
	if (!!contentId) {
		const collabDraftPromise = preloadCollabDraftContent(contentId, '', false);
		preloaders.push(collabDraftPromise);

		preloaders.push(
			preloadMacroDataFromCollabDraft({
				collabDraftPromise,
				context: { contentId, spaceKey: spaceKey || '' },
				route: 'live',
			}),
		);

		preloaders.push(preloadEditorNodeDataProvidersData(contentId, cloudId, false));

		if (
			expValEquals('confluence_compact_text_format', 'isEnabled', true) ||
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			(expValEquals('cc_editor_ai_content_mode', 'variant', 'test') &&
				fg('platform_editor_content_mode_button_mvp'))
		) {
			// Get isArchived from cached page data
			const { isArchived } = getCachedPageData(contentId ?? undefined);
			preloaders.push(
				preloadContentMode({ contentId, isDraft: false, useCache: false, isArchived }),
			);
		}

		preloaders.push(preloadMentionsProvider());
	}

	preloaders.push(
		// Pre-loading apps in the byline area for live pages. Currently only live pages have extension point apps and not classic editor.
		preloadWebItemLocation({ contentId, location: SYSTEM_CONTENT_BYLINE }),
		preloadUseExtensionList({
			spaceKey: spaceKey || '',
			moduleType: FORGE_MODULE_BYLINE,
		}),
	);

	preloaders.push(preloadReadTime({ contentId, useCache: false }));

	// 2. Unified data
	return waitForAllReturnWhenError([
		query<preloadLivePageDataQuery$data, preloadLivePageDataQuery$variables>({
			query: preloadLivePageDataQuery,
			variables: {
				userId,
				contentId,
				spaceKey,
				isUsdSsr: fg('confluence_frontend_usd_ssr'),
			},
			fetchPolicy: 'network-only',
		}).then((response) => {
			if (response.data) {
				const nativeCollabVariables = {
					contentId,
					status: ['draft', 'current'],
				};

				writeQueryInterop<NativeCollabQueryResponse, NativeCollabQueryVariables>({
					query: NativeCollabQuery,
					variables: nativeCollabVariables,
					data: response.data as any,
				});
			}
		}),
		...preloaders,
	]);
}
